/* eslint-disable import/no-restricted-paths */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Modal,
  Typography,
  LoadingIndicator,
} from "@sunrun/experience-ui-components";
import { Contact } from "@sunrun/experience-ui-components/lib/components/ContactCard";
import {
  HeaderButton,
  HeaderContentLayout,
} from "../templates/HeaderContentLayout";
import { SplatProspectUpdate } from "../../../amplify/backend/function/OfferExpUsage/ts/public/types";
import { type ContractReviewMessage } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { subscribeToAsyncRequest } from "utils/subscribeToAsyncRequest";
import { SplatProspectResponse } from "providers/Types";
import {
  deleteAttachment,
  patchUsage,
  postAttachment,
} from "services/prospect";
import { useAppSelector } from "store";
import {
  Attachment,
  determineUpdatedValue,
  isEmptyValue,
  getMonthlyUsage,
  monthKeys,
  monthsAndDays,
  ROLES,
  UsageCadence,
  UtilityInfoState,
  dailyCompanies,
} from "utils/usageUtils";
import { useContactsData, useUsageData, useUtilityData } from "utils/swrHooks";
import { utilityInfoFormSchema } from "providers/schema";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { UsageSlideoutContent } from "components/organisms/UsageSlideoutContent";
import { ErrorModal } from "components/molecules/ErrorModal";
import { editContact } from "services/contacts";
import { SalesforceUpdatedContext } from "providers/NewOfferProvider";
import { UsageFormContext } from "providers/UsageFormContext";
import { useOfferList, useProspect } from "checkout/hooks/useCheckoutSwr";
import { findContact } from "utils/contacts";
import { regenDocs } from "services/prospect";
import { useProposals } from "utils/swrHooks";
import { Slideout } from "components/molecules/ConfirmInfo/Slideout";
import { UsageType } from "constants/usageTypes";

const UsageSlideout = ({
  onClose,
  onSave,
  onGetBillOnline,
  intervalDataInitialLoading,
  intervalDataStatus,
  isSunrunManaged,
  contractReviewMessages = [],
  utilityBillPastDueBalanceFlowIsEnabled,
  hasUploadedUtilityBillFileWithPastDueBalance,
  utilityPastDueBalance,
  utilityCompany,
}: {
  onClose: (isDismissed?: boolean) => void;
  onSave?: () => void;
  onGetBillOnline?: (hasProposals: boolean) => void;
  intervalDataInitialLoading?: boolean;
  intervalDataStatus?: string | null;
  isSunrunManaged?: boolean;
  contractReviewMessages?: ContractReviewMessage[];
  utilityBillPastDueBalanceFlowIsEnabled: boolean;
  hasUploadedUtilityBillFileWithPastDueBalance: boolean;
  utilityPastDueBalance: number;
  utilityCompany: string;
}) => {
  const usageContext = useContext(UsageFormContext);
  const salesforceUpdated = useContext(SalesforceUpdatedContext);
  const { prospectId } = useParams();
  const [originalUsage, setOriginalUsage] =
    useState<SplatProspectResponse | null>(null);
  const [originalDailyUsage, setOriginalDailyUsage] = useState<
    (number | undefined)[]
  >(new Array(12).fill(undefined));
  const [isPatchLoading, setIsPatchLoading] = useState(false);
  const [monthlyUsage, setMonthlyUsage] = useState<
    null | (undefined | number)[]
  >(new Array(12).fill(undefined));
  const [dailyUsage, setDailyUsage] = useState(new Array(12).fill(undefined));
  const [usageCadence, setUsageCadence] = useState<UsageCadence>("monthly");
  const [usageType, setUsageType] = useState<UsageType>("full");
  const [homeSize, setHomeSize] = useState<number>(0);
  const [showVoidProposalModal, setShowVoidProposalModal] = useState(false);
  const [modifiedField, setModifiedField] = useState<string | null>(null);
  const { data: prospect } = useProspect(prospectId);

  const [intervalDataDeclinedReason, setIntervalDataDeclinedReason] = useState<
    string | undefined
  >(undefined);

  const authKey = useAppSelector((state) => state?.auth?.hybridToken);
  const { refetch: contactsRefetch, contacts } = useContactsData(
    prospectId!,
    authKey
  );
  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );

  const { data: proposals } = useProposals(prospectId!, authKey);

  useEffect(() => {
    if (contacts?.length) {
      if (!selectedUtilityContact) {
        setSelectedUtilityContact(
          findContact(contacts, ROLES.UTILITYBILLCONTACT)
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts]);

  const { offerList, isOfferListLoading } = useOfferList(
    prospectId!,
    cognitoToken
  );

  const hasOffers = !!proposals?.length || !!offerList?.length;
  const hasSignedOffer = useMemo(() => {
    return !!proposals?.find(({ signed }) => signed);
  }, [proposals]);

  const formUtils = useForm({
    resolver: zodResolver(utilityInfoFormSchema),
    defaultValues: {
      accountNumber: "",
      meterNumber: "",
      rateSchedule: "",
      utilityBillDate: "",
      utilityCompany: "",
      utilityServiceAddress: "",
    } as UtilityInfoState,
  });
  const [patchErrorMessage, setPatchErrorMessage] = useState<null | Error>(
    null
  );
  const [deleteError, setDeleteError] = useState<Error | null>(null);
  const [isDocumentLoading, setIsDocumentLoading] = useState(false);
  const [attachmentErrorMessage, setAttachmentErrorMessage] =
    useState<Error | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [selectedUtilityContact, setSelectedUtilityContact] =
    useState<Contact>();
  const [utilityContactChanged, setUtilityContactChanged] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const hydratedForProspectIdRef = useRef<string | undefined>();

  const {
    prospect: usage,
    attachments: attachmentsData,
    error: usageError,
    isLoading: isUsageLoading,
    refetch: usageRefetch,
  } = useUsageData(prospectId);

  const [intervalDataDeclined, intervalDataUsed, intervalDataRequired] =
    useMemo(() => {
      return [
        usage?.intervalDataDeclined ?? false,
        usage?.intervalDataUsed ?? false,
        ["PG&E", "SDG&E", "SCE"].includes(usage?.utilityCompany || ""),
      ];
    }, [usage]);

  const {
    data: utilitiesOptions,
    error: utilityError,
    isLoading: isUtilitiesLoading,
  } = useUtilityData(usage?.customerState as string, authKey);

  useEffect(() => {
    if (Array.isArray(attachmentsData)) {
      setAttachments(attachmentsData);
    }
  }, [attachmentsData]);

  const handleDeleteUtilityBill = useCallback(
    async (file: Attachment & { documentId: string }) => {
      if (!prospectId || !authKey) return;

      try {
        await deleteAttachment(authKey, prospectId, file.documentId);
        await usageRefetch();
      } catch (err) {
        setDeleteError(err as Error);
        throw err;
      }
    },
    [prospectId, authKey, usageRefetch]
  );

  const immediateUpload = useMemo(
    () =>
      prospectId && authKey
        ? {
            upload: (attachmentsToUpload: Attachment[]) =>
              Promise.all(
                attachmentsToUpload.map((attachment) =>
                  postAttachment(authKey, prospectId, attachment)
                )
              ).then(() => undefined),
            onSuccess: () => usageRefetch(),
            onError: setAttachmentErrorMessage,
            onLoadingChange: setIsDocumentLoading,
          }
        : undefined,
    [authKey, prospectId, usageRefetch]
  );

  useEffect(() => {
    if (!usage || !prospectId) {
      return;
    }

    if (hydratedForProspectIdRef.current === prospectId) {
      return;
    }

    hydratedForProspectIdRef.current = prospectId;

    setOriginalUsage(usage);
    if (intervalDataRequired) {
      setIntervalDataDeclinedReason(usage.intervalDataDeclinedReason);
    }
    formUtils.reset({
      accountNumber: usage.accountNumber,
      meterNumber: usage.meterNumber,
      rateSchedule: usage.rateSchedule,
      utilityBillDate: usage.utilityBillDate,
      utilityCompany: usage.utilityCompany,
      utilityServiceAddress: usage.utilityServiceAddress,
    });

    const cadence = dailyCompanies.includes(usage?.utilityCompany || "")
      ? "daily"
      : "monthly";
    setUsageCadence(cadence);

    const monthlyUsageList = getMonthlyUsage(usage);
    setMonthlyUsage(monthlyUsageList);

    const dailyUsageList = monthlyUsageList.map(
      (val: number | undefined, index: number) => {
        return val && val / monthsAndDays[index].days;
      }
    );

    setOriginalDailyUsage(dailyUsageList);
    setDailyUsage(dailyUsageList);

    if (intervalDataRequired) {
      setIntervalDataDeclinedReason(usage.intervalDataDeclinedReason);
    }

    setHomeSize(usage.homeSize || 0);
  }, [usage, prospectId, formUtils, intervalDataRequired]);

  const calculateDailyUsage = useCallback((): (number | undefined)[] => {
    return monthsAndDays.map((monthObj, index: number) => {
      const dailyUsageVal = dailyUsage[index];
      if (dailyUsageVal === undefined) {
        return undefined;
      }
      return dailyUsageVal * monthObj.days;
    });
  }, [dailyUsage]);

  const saveUtilityContact = useCallback(
    async (contact: Contact) => {
      if (!prospectId) {
        throw new Error("No prospect id is present.");
      }
      if (!selectedUtilityContact?.contactId) {
        throw new Error("No contact id is present for your utility contact.");
      }
      await editContact(
        authKey,
        prospectId,
        selectedUtilityContact?.contactId,
        contact.customerPrimaryPhone,
        contact.customerEmail,
        { role: ROLES.UTILITYBILLCONTACT }
      );
      await contactsRefetch();
    },
    [authKey, prospectId, selectedUtilityContact, contactsRefetch]
  );

  const diffInUsageBy = useCallback(
    (keys: (keyof Exclude<typeof originalUsage, null>)[], fieldValues: any) => {
      if (originalUsage) {
        return keys.reduce<typeof keys>(
          (diff, key) =>
            fieldValues[key] !== originalUsage[key] ? diff.concat(key) : diff,
          []
        );
      }
      return [];
    },
    [originalUsage]
  );

  const {
    monthlyUsageChanged,
    calculatedMonthlyValues,
    calculatedMonthlyValuesObj,
  } = useMemo(() => {
    const calculatedMonthlyValues =
      usageCadence === "monthly" ? monthlyUsage : calculateDailyUsage();

    const calculatedMonthlyValuesObj =
      usageType === "squareFootage"
        ? {
            janUsage: originalUsage?.janUsage ?? null,
            febUsage: originalUsage?.febUsage ?? null,
            marUsage: originalUsage?.marUsage ?? null,
            aprUsage: originalUsage?.aprUsage ?? null,
            mayUsage: originalUsage?.mayUsage ?? null,
            junUsage: originalUsage?.junUsage ?? null,
            julUsage: originalUsage?.julUsage ?? null,
            augUsage: originalUsage?.augUsage ?? null,
            sepUsage: originalUsage?.sepUsage ?? null,
            octUsage: originalUsage?.octUsage ?? null,
            novUsage: originalUsage?.novUsage ?? null,
            decUsage: originalUsage?.decUsage ?? null,
          }
        : {
            janUsage: calculatedMonthlyValues?.[0] ?? null,
            febUsage: calculatedMonthlyValues?.[1] ?? null,
            marUsage: calculatedMonthlyValues?.[2] ?? null,
            aprUsage: calculatedMonthlyValues?.[3] ?? null,
            mayUsage: calculatedMonthlyValues?.[4] ?? null,
            junUsage: calculatedMonthlyValues?.[5] ?? null,
            julUsage: calculatedMonthlyValues?.[6] ?? null,
            augUsage: calculatedMonthlyValues?.[7] ?? null,
            sepUsage: calculatedMonthlyValues?.[8] ?? null,
            octUsage: calculatedMonthlyValues?.[9] ?? null,
            novUsage: calculatedMonthlyValues?.[10] ?? null,
            decUsage: calculatedMonthlyValues?.[11] ?? null,
          };
    const monthlyUsageChanged =
      diffInUsageBy(
        [
          "janUsage",
          "febUsage",
          "marUsage",
          "aprUsage",
          "mayUsage",
          "junUsage",
          "julUsage",
          "augUsage",
          "sepUsage",
          "octUsage",
          "novUsage",
          "decUsage",
        ],
        calculatedMonthlyValuesObj
      ).length > 0;
    return {
      monthlyUsageChanged,
      calculatedMonthlyValues,
      calculatedMonthlyValuesObj,
    };
  }, [
    originalUsage,
    usageCadence,
    usageType,
    monthlyUsage,
    calculateDailyUsage,
    diffInUsageBy,
  ]);

  const handleSave = useCallback(
    (
      voidProposals: boolean | undefined = undefined,
      regenerateDocuments: boolean | undefined = undefined
    ) => {
      if (!originalUsage) {
        return;
      }
      if (monthlyUsage && prospectId) {
        const formValues = formUtils.getValues();

        const formValuesChanged =
          diffInUsageBy(
            [
              "accountNumber",
              "meterNumber",
              "utilityBillDate",
              "utilityServiceAddress",
              "rateSchedule",
              "utilityCompany",
            ],
            formValues
          ).length > 0;

        const intervalDataDeclinedReasonChanged =
          intervalDataRequired &&
          usage?.intervalDataDeclinedReason !== intervalDataDeclinedReason;

        const prospectBody: SplatProspectUpdate = {
          ...calculatedMonthlyValuesObj,
          ...(intervalDataDeclinedReasonChanged
            ? { intervalDataDeclinedReason }
            : {}),
          ...(usageType === "squareFootage" ? { homeSize } : {}),
          ...(voidProposals ? { voidProposals } : undefined),
          ...formValues,
        };

        if (
          formValues.utilityServiceAddress !==
          originalUsage.utilityServiceAddress
        ) {
          prospectBody.partialOverrideAddressStandardization = true;
        }

        setIsPatchLoading(true);

        const usagePromise =
          monthlyUsageChanged ||
          formValuesChanged ||
          intervalDataDeclinedReasonChanged
            ? patchUsage(authKey, prospectId, prospectBody).then((result) =>
                subscribeToAsyncRequest(authKey, result.asyncRequestId)
              )
            : new Promise((res) => res(true));

        const contactPromisesArr: Promise<any>[] = [];
        if (selectedUtilityContact && utilityContactChanged) {
          contactPromisesArr.push(saveUtilityContact(selectedUtilityContact));
        }

        Promise.all([usagePromise, ...contactPromisesArr])
          .then(async () => {
            if (regenerateDocuments) {
              await regenDocs({ authKey, prospectId });
            }
          })
          .then(async () => {
            await Promise.all([usageRefetch(), contactsRefetch()]);

            const updatedFields = [];
            let key: keyof UtilityInfoState;
            for (key in formValues) {
              if (determineUpdatedValue(formValues[key], originalUsage[key])) {
                updatedFields.push(key);
              }
            }
            for (let i = 0; i < monthKeys.length; i++) {
              let monthKey = monthKeys[i];
              if (
                determineUpdatedValue(
                  calculatedMonthlyValues?.[i],
                  originalUsage[monthKey as keyof SplatProspectResponse]
                )
              ) {
                updatedFields.push(monthKey);
              }
            }
            salesforceUpdated(updatedFields);
            setIsPatchLoading(false);
            onSave?.();
            onClose();
          })
          .catch((err) => {
            setShowVoidProposalModal(false);
            setIsPatchLoading(false);
            setPatchErrorMessage(err);
          });
      }
    },
    [
      originalUsage,
      onClose,
      onSave,
      monthlyUsage,
      prospectId,
      formUtils,
      authKey,
      selectedUtilityContact,
      saveUtilityContact,
      contactsRefetch,
      usageRefetch,
      salesforceUpdated,
      utilityContactChanged,
      diffInUsageBy,
      usageType,
      homeSize,
      monthlyUsageChanged,
      calculatedMonthlyValuesObj,
      calculatedMonthlyValues,
      intervalDataDeclinedReason,
      intervalDataRequired,
      usage,
    ]
  );

  const checkIfVoidProposal = useCallback((): true | void => {
    if (!originalUsage) {
      return;
    }

    if (
      intervalDataRequired &&
      !originalUsage.intervalDataDeclinedReason &&
      !intervalDataDeclinedReason &&
      monthlyUsageChanged
    ) {
      usageContext.setIntervalDeclineReasonError(true);
      return;
    }

    if (
      selectedUtilityContact &&
      utilityContactChanged &&
      (isEmptyValue(selectedUtilityContact.customerEmail) ||
        isEmptyValue(selectedUtilityContact.customerPrimaryPhone))
    ) {
      setPatchErrorMessage(
        new Error(
          "The Utility Contact selected needs to have both an email and phone number on file."
        )
      );
      return;
    }

    const getFormData = formUtils.getValues();
    for (const key in getFormData) {
      if (
        key === "accountNumber" ||
        key === "meterNumber" ||
        key === "utilityServiceAddress" ||
        key === "utilityBillDate"
      ) {
        continue;
      }

      if (
        hasOffers &&
        determineUpdatedValue(
          getFormData[key as keyof UtilityInfoState],
          originalUsage[key as keyof UtilityInfoState]
        )
      ) {
        setShowVoidProposalModal(true);
        setModifiedField(key);
        return true;
      }
    }

    const currentUsage = usageCadence === "monthly" ? monthlyUsage : dailyUsage;

    for (let i = 0; i < monthKeys.length; i++) {
      const monthKey = monthKeys[i];
      const currentOriginalUsage =
        usageCadence === "monthly"
          ? originalUsage[monthKey as keyof SplatProspectResponse]
          : originalDailyUsage[i];

      if (
        hasOffers &&
        determineUpdatedValue(currentUsage![i], currentOriginalUsage)
      ) {
        setShowVoidProposalModal(true);
        setModifiedField(monthKey);
        return true;
      }
    }

    handleSave();
  }, [
    utilityContactChanged,
    selectedUtilityContact,
    originalUsage,
    usageCadence,
    formUtils,
    handleSave,
    hasOffers,
    monthlyUsage,
    intervalDataRequired,
    intervalDataDeclinedReason,
    monthlyUsageChanged,
    usageContext,
    dailyUsage,
    originalDailyUsage,
  ]);

  const handleCancel = useCallback(() => {
    if (usageContext.isDirty) {
      setShowExitModal(true);
    } else onClose(true);
  }, [usageContext.isDirty, onClose]);

  const checkForFormErrors = useCallback(() => {
    if (isDocumentLoading) {
      return;
    }
    return formUtils.handleSubmit(checkIfVoidProposal)();
  }, [checkIfVoidProposal, formUtils, isDocumentLoading]);

  const buttons = useMemo(() => {
    return [
      {
        type: "secondary",
        text: "Close",
        onClick: handleCancel,
      } as HeaderButton,
      {
        type: "primary",
        text: "Save",
        onClick: checkForFormErrors,
        disabled: isDocumentLoading,
      } as HeaderButton,
    ];
  }, [checkForFormErrors, handleCancel, isDocumentLoading]);

  if (usageError || utilityError) {
    return (
      <HeaderContentLayout
        content={
          <ErrorComponent
            context="UsagePage"
            action={usageError ? "Get Usage" : "Get Utility"}
            error={usageError ?? utilityError}
          />
        }
        title="There was a Problem."
        description={`Something went wrong. Please try refreshing the page.`}
      />
    );
  }

  if (
    isUsageLoading ||
    isUtilitiesLoading ||
    isPatchLoading ||
    isOfferListLoading
  ) {
    return (
      <Slideout>
        <LoadingIndicator fullScreen color="black" />;
      </Slideout>
    );
  }

  return (
    <Slideout onClose={onClose}>
      {!!patchErrorMessage && (
        <ErrorModal
          context="UsagePage"
          error={patchErrorMessage}
          action={"Saving Usage & Utility"}
          onClose={() => setPatchErrorMessage(null)}
        />
      )}
      {!!deleteError && (
        <ErrorModal
          context="UsageSlideout"
          error={deleteError}
          action="Deleting Utility Bill"
          onClose={() => setDeleteError(null)}
        />
      )}
      {!!attachmentErrorMessage && (
        <ErrorModal
          context="UsageSlideout"
          error={attachmentErrorMessage}
          action="Uploading Utility Bill"
          onClose={() => setAttachmentErrorMessage(null)}
        />
      )}
      {showExitModal && usageContext.isDirty && (
        <Modal
          testId="usage-slideout-exit-modal"
          type="warning"
          title="Are you sure you want to exit without saving?"
          onClose={() => setShowExitModal(false)}
          primaryButton={{
            text: "Save & Close",
            onClick: () => handleSave(),
          }}
          secondaryButton={{
            text: "Close",
            onClick: () => onClose(),
          }}
        >
          <Typography variant="p">
            All of your unsaved changes will be discarded.
          </Typography>
        </Modal>
      )}
      <HeaderContentLayout
        title="Usage & Utility"
        description="To find your best system, we need to understand your current electricity rate."
        buttons={buttons}
        smallPadding={true}
        content={
          <UsageSlideoutContent
            utilityBillPastDueBalanceFlowIsEnabled={
              utilityBillPastDueBalanceFlowIsEnabled
            }
            hasUploadedUtilityBillFileWithPastDueBalance={
              hasUploadedUtilityBillFileWithPastDueBalance
            }
            utilityCompany={utilityCompany}
            utilityPastDueBalance={utilityPastDueBalance}
            additionalSystem={prospect?.additionalSystem}
            isSunrunManaged={isSunrunManaged}
            showUtilityBillWarningBanner={
              usageContext.showUtilityBillWarningBanner
            }
            setShowUtilityBillWarningBanner={
              usageContext.setShowUtilityBillWarningBanner
            }
            monthlyUsage={
              usageCadence === "monthly" ? monthlyUsage : dailyUsage
            }
            setMonthlyUsage={
              usageCadence === "monthly" ? setMonthlyUsage : setDailyUsage
            }
            showVoidProposalModal={showVoidProposalModal}
            setShowVoidProposalModal={setShowVoidProposalModal}
            handleSave={handleSave}
            modifiedField={modifiedField}
            utilitiesOptions={utilitiesOptions}
            formUtils={formUtils}
            usageCadence={usageCadence}
            setUsageCadence={setUsageCadence}
            usageType={usageType}
            setUsageType={setUsageType}
            homeSize={homeSize}
            setHomeSize={setHomeSize}
            attachments={attachments}
            immediateUpload={immediateUpload}
            selectedUtilityContact={selectedUtilityContact}
            setSelectedUtilityContact={(contact) => {
              setUtilityContactChanged(true);
              setSelectedUtilityContact(contact);
            }}
            hasOffers={hasOffers}
            hasSignedOffer={hasSignedOffer}
            refetchContacts={contactsRefetch}
            usage={usage}
            onGetBillOnline={
              onGetBillOnline ? () => onGetBillOnline(hasOffers) : undefined
            }
            intervalDataInitialLoading={intervalDataInitialLoading}
            intervalDataStatus={intervalDataStatus}
            contractReviewMessages={contractReviewMessages}
            onDeleteUtilityBill={handleDeleteUtilityBill}
            {...(intervalDataRequired
              ? {
                  intervalDataDeclined: intervalDataDeclined,
                  intervalDataDeclinedReason: intervalDataDeclinedReason,
                  setIntervalDataDeclinedReason: intervalDataRequired
                    ? setIntervalDataDeclinedReason
                    : undefined,
                  intervalDataUsed: intervalDataUsed,
                }
              : {})}
          />
        }
      />
    </Slideout>
  );
};

export { UsageSlideout };
